import React, { useEffect, useState, useMemo } from 'react';
import { getLogFiles, getLogFileContent } from 'apis/controllers/logViewer';
import ReactJson from 'react-json-view';

const LogContentModal = ({ fileName, content, onClose }) => {
    const [searchTerm, setSearchTerm] = useState('');

    // Parse JSON lines into objects
    const entries = useMemo(() => {
        console.log(typeof content, content);
        if (typeof content === 'string') {
            return content
                .split(/\r?\n/)
                .filter((line) => line.trim() !== '')
                .map((line, i) => {
                    try {
                        return JSON.parse(line);
                    } catch {
                        return { error: 'Invalid JSON', raw: line };
                    }
                });
        } else if (typeof content === 'object' && content !== null) {
            // content is already an object, just return it as an array for consistency
            return [content];
        } else {
            // handle unexpected types
            return [];
        }
    }, [content]); 

    // Filter entries by search term (case-insensitive substring match in JSON string)
    const filteredEntries = useMemo(() => {
        if (!searchTerm.trim()) return entries;
        return entries.filter((entry) => JSON.stringify(entry).toLowerCase().includes(searchTerm.toLowerCase()));
    }, [entries, searchTerm]); 

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                background: 'rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 40
            }}
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: '#fff',
                    borderRadius: 8,
                    maxWidth: 900,
                    width: '95%',
                    maxHeight: '80vh',
                    overflowY: 'auto',
                    padding: 24,
                    boxShadow: '0 2px 16px #aaa',
                    position: 'relative',
                    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif'
                }}
            >
                <button
                    onClick={onClose}
                    title="Close"
                    style={{
                        position: 'absolute',
                        right: 12,
                        top: 12,
                        background: '#1976d2',
                        border: 'none',
                        borderRadius: '50%',
                        width: 32,
                        height: 32,
                        color: 'white',
                        fontSize: 20,
                        fontWeight: 'bold',
                        cursor: 'pointer'
                    }}
                >
                    ×
                </button>
                <h2 style={{ color: '#1976d2', marginBottom: 16 }}>{fileName}</h2>

                <input
                    type="text"
                    placeholder="Search log entries..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                        marginBottom: 16,
                        padding: '8px 12px',
                        width: '100%',
                        fontSize: 16,
                        borderRadius: 6,
                        border: '1.5px solid #bdbdbd',
                        outline: 'none'
                    }}
                />

                {filteredEntries.length === 0 && <p>No log entries match your search.</p>}

                <div>
                    {filteredEntries.map((entry, idx) => (
                        <div
                            key={idx}
                            style={{
                                borderBottom: '1px solid #eee',
                                paddingBottom: 12,
                                marginBottom: 12,
                                fontSize: 14,
                                backgroundColor: '#f9f9f9',
                                borderRadius: 6,
                                boxShadow: 'inset 0 0 5px rgba(0,0,0,0.03)'
                            }}
                        >
                            <ReactJson
                                src={entry}
                                collapsed={2}
                                enableClipboard={false}
                                displayDataTypes={false}
                                name={false}
                                groupArraysAfterLength={10}
                                style={{ fontSize: 14 }}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const LogViewer = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [serviceFilter, setServiceFilter] = useState('');
    const [createdFilter, setCreatedFilter] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [selectedFileContent, setSelectedFileContent] = useState(null);
    const [selectedFileName, setSelectedFileName] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    useEffect(() => {
        getLogFiles()
            .then((data) => {
                setLogs(data);
                setLoading(false);
            })
            .catch((err) => {
                setError(err.message);
                setLoading(false);
            });
    }, []);

    // Filter by service and creationDateShort (not creationTime)
    const filteredLogs = logs.filter((log) => {
        const serviceMatch = log.serviceName ? log.serviceName.toLowerCase().includes(serviceFilter.toLowerCase()) : false;
        const createdMatch = log.creationDateShort ? log.creationDateShort.includes(createdFilter) : false;
        return serviceMatch && createdMatch;
    });

    // Sorting logic same as before...
    const sortedLogs = React.useMemo(() => {
        if (!sortConfig.key) return filteredLogs;
        const sorted = [...filteredLogs].sort((a, b) => {
            let valA = a[sortConfig.key];
            let valB = b[sortConfig.key];
            if (sortConfig.key.includes('Time')) {
                valA = new Date(valA).getTime();
                valB = new Date(valB).getTime();
            }
            if (typeof valA === 'number' && typeof valB === 'number') {
                return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
            } else {
                return sortConfig.direction === 'asc' ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
            }
        });
        return sorted;
    }, [filteredLogs, sortConfig]);

    const handleSort = (key) => {
        setSortConfig((current) => {
            if (current.key === key) {
                return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
    };

    // Double-click to open file content
    const handleRowDoubleClick = async (fileName) => {
        try {
            setSelectedFileName(fileName);
            const content = await getLogFileContent(fileName);
            setSelectedFileContent(content);
            setModalOpen(true);
        } catch (err) {
            setSelectedFileContent('Failed to load file: ' + err.message);
            setModalOpen(true);
        }
    };

    // Close modal
    const closeModal = () => {
        setModalOpen(false);
        setSelectedFileName(null);
        setSelectedFileContent(null);
    };

    if (loading) return <div>Loading log files...</div>;
    if (error) return <div style={{ color: 'red' }}>{error}</div>;

    return (
        <div
            style={{
                maxWidth: 1200,
                margin: '0 auto',
                background: '#f9f9f9',
                borderRadius: 8,
                boxShadow: '0 1.5px 8px #ececec',
                padding: 24
            }}
        >
            <h1 style={{ fontWeight: 700, color: '#1976d2', fontSize: 22, marginBottom: 16 }}>Log Viewer</h1>
            <div style={{ marginBottom: 12, display: 'flex', gap: 18 }}>
                <label>
                    <span style={{ color: '#555', marginRight: 6 }}>Filter by Service Name:</span>
                    <input
                        type="text"
                        value={serviceFilter}
                        onChange={(e) => setServiceFilter(e.target.value)}
                        placeholder="Enter service name..."
                        style={{ padding: '4px 8px', borderRadius: 4, border: '1.2px solid #bdbdbd', marginRight: 12 }}
                    />
                </label>
                <label>
                    <span style={{ color: '#555', marginRight: 6 }}>Filter by Created (YYYY-MM-DD):</span>
                    <input
                        type="text"
                        value={createdFilter}
                        onChange={(e) => setCreatedFilter(e.target.value)}
                        placeholder="2025-09-09"
                        style={{ padding: '4px 8px', borderRadius: 4, border: '1.2px solid #bdbdbd', marginRight: 12 }}
                    />
                </label>
            </div>
            <table
                style={{
                    borderCollapse: 'collapse',
                    width: '100%',
                    background: 'white',
                    borderRadius: 6,
                    overflow: 'hidden',
                    fontSize: 16
                }}
            >
                <thead>
                    <tr>
                        <th
                            style={{
                                borderBottom: '1.5px solid #bdbdbd',
                                textAlign: 'left',
                                padding: '8px',
                                cursor: 'pointer',
                                fontWeight: 600,
                                background: '#fafafa'
                            }}
                            onClick={() => handleSort('fileName')}
                        >
                            File Name
                        </th>
                        <th
                            style={{
                                borderBottom: '1.5px solid #bdbdbd',
                                textAlign: 'left',
                                padding: '8px',
                                cursor: 'pointer',
                                fontWeight: 600,
                                background: '#fafafa'
                            }}
                            onClick={() => handleSort('serviceName')}
                        >
                            Service Name
                        </th>
                        <th
                            style={{
                                borderBottom: '1.5px solid #bdbdbd',
                                textAlign: 'left',
                                padding: '8px',
                                cursor: 'pointer',
                                fontWeight: 600,
                                background: '#fafafa'
                            }}
                            onClick={() => handleSort('size')}
                        >
                            Size
                        </th>
                        <th
                            style={{
                                borderBottom: '1.5px solid #bdbdbd',
                                textAlign: 'left',
                                padding: '8px',
                                cursor: 'pointer',
                                fontWeight: 600,
                                background: '#fafafa'
                            }}
                            onClick={() => handleSort('creationTime')}
                        >
                            Created
                        </th>
                        <th
                            style={{
                                borderBottom: '1.5px solid #bdbdbd',
                                textAlign: 'left',
                                padding: '8px',
                                cursor: 'pointer',
                                fontWeight: 600,
                                background: '#fafafa'
                            }}
                            onClick={() => handleSort('lastWriteTime')}
                        >
                            Modified
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {sortedLogs.map((log) => (
                        <tr
                            key={log.fileName}
                            style={{ borderBottom: '1px solid #eeeeee', transition: 'background 0.15s', cursor: 'pointer' }}
                            onDoubleClick={() => handleRowDoubleClick(log.fileName)}
                            title="Double-click to open this log"
                        >
                            <td style={{ padding: '8px' }}>{log.fileName}</td>
                            <td style={{ padding: '8px' }}>{log.serviceName}</td>
                            <td style={{ padding: '8px' }}>{log.size}</td>
                            <td style={{ padding: '8px' }}>{new Date(log.creationTime).toLocaleString()}</td>
                            <td style={{ padding: '8px' }}>{new Date(log.lastWriteTime).toLocaleString()}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {sortedLogs.length === 0 && (
                <div style={{ marginTop: 24, color: '#b71c1c', fontWeight: 500 }}>No log files match your filters.</div>
            )}

            {/* Modal for log content */}
            {modalOpen && <LogContentModal fileName={selectedFileName} content={selectedFileContent} onClose={closeModal} />}
        </div>
    );
};

export default LogViewer;
